import { apiFetch } from "../api/client";
import type { Extended } from "./types";
import type { Rows } from "./store";

/**
 * THE EVENT'S OWN SETTINGS, read from the API.
 *
 * Everything arrives in ONE public call: `GET /context/:token` carries
 * `settingsAll`, the raw `extended` of every language the event has, keyed by
 * language KEY — `{ FR: {…}, EN: {…}, IT: {…} }` — `typosV2` included. That is
 * exactly what the cascade consumes; no back-office view to unpick, no
 * authentication to obtain first.
 *
 * ⚠️ ROWS ARE KEYED BY LANGUAGE ID, not by key, because that is what writing
 * needs: `PUT /settings/fo` takes a `languageId`. The mapping comes from
 * `GET /languages` on apix, public as well. Two public calls, no session — which
 * matters: a participant reaching a sign-in screen must already see the event's
 * own words on it.
 */

export type Language = { id: number; key: string; name: string; short: string };

type RawLanguage = { id?: number; key?: string; label?: string };

/** The reference language when the event names none. V1 keeps FR whatever happens. */
export const REFERENCE_KEY = "FR";

/**
 * V2's OWN keys, deliberately not V1's.
 *
 * The event already carries `FO_MEETING_LANGUAGES_SHOW` and
 * `FO_MEETING_LANGUAGE_DEFAULT`; writing those would change the site
 * participants are on today. V2 keeps its own list, so an organiser can prepare
 * a version in three languages while the live one stays on two.
 */
export const SHOW_KEY = "FO_MEETING_V2_LANGUAGES_SHOW";
export const DEFAULT_KEY = "FO_MEETING_V2_LANGUAGE_DEFAULT";

/**
 * A structural setting, looked up wherever it happens to live.
 *
 * Which languages exist is not itself translatable, so it is written once, into
 * whichever row the organiser happened to be reading. Scanning them all costs
 * nothing and avoids a site with no languages because the value landed one row
 * over.
 *
 * ⚠️ Takes rows keyed EITHER WAY — by language key, as the API sends them, or by
 * language id, as the panel holds them. It only ever looks at the values, and
 * that is what lets the live rows drive the language list: derived from the API
 * payload instead, turning a language on changed nothing until a reload.
 */
function structural(
  rows: Record<string | number, Extended | null> | undefined,
  key: string,
): unknown {
  for (const row of Object.values(rows ?? {})) {
    if (row?.[key] !== undefined) return row[key];
  }
  return undefined;
}

export async function fetchLanguages(
  signal?: AbortSignal,
): Promise<Language[]> {
  const answer = await apiFetch<{ languages?: RawLanguage[] }>("/languages", {
    api: "apix",
    signal,
  });

  return (answer?.languages ?? [])
    .filter(
      (l): l is RawLanguage & { id: number; key: string } =>
        typeof l?.id === "number" && typeof l.key === "string",
    )
    .map((l) => ({
      id: l.id,
      key: l.key,
      // The labels carry a flag ("Français  🇫🇷"). Stripped: the panel puts them
      // in a select beside plain text and the flags line up with nothing.
      // ⚠️ Regional indicators (🇫🇷 is U+1F1EB U+1F1F7) are NOT
      // `Extended_Pictographic`, so they need their own range — a rule that
      // matched only pictographs left every flag standing.
      name: (l.label ?? l.key)
        .replace(/[\p{Extended_Pictographic}\u{1F1E6}-\u{1F1FF}\uFE0F]/gu, "")
        .replace(/\s+/g, " ")
        .trim(),
      short: l.key,
    }));
}

/**
 * `settingsAll`, re-keyed by language id and reduced to the languages the event
 * actually uses.
 *
 * A language the catalogue knows but the event never filled in is NOT invented
 * here: an empty row would read as "configured, and empty", and the panel would
 * offer to clear values that were never set.
 */
export function rowsFrom(
  settingsAll: Record<string, Extended | null> | undefined,
  languages: ReadonlyArray<Language>,
): Rows {
  const byKey = new Map(languages.map((l) => [l.key, l.id]));
  const rows: Rows = {};
  for (const [key, extended] of Object.entries(settingsAll ?? {})) {
    const id = byKey.get(key);
    if (id !== undefined && extended) rows[id] = extended;
  }
  return rows;
}

/**
 * The languages the event has TURNED ON, in the catalogue's order.
 *
 * ⚠️ NOT "the languages that have a row". This event has rows for FR, EN and IT
 * but only lists FR and EN: a row survives from a language somebody tried once
 * and switched off, and offering it back would let an organiser write into a
 * language no participant can ever select.
 *
 * The list falls back to the rows only when the event names none at all — an
 * older event that predates the setting still has to show something.
 */
export function languagesOf(
  rows: Record<string | number, Extended | null> | undefined,
  languages: ReadonlyArray<Language>,
  /** Keys the event has a row for — the fallback when it names no list. */
  fallbackKeys: ReadonlyArray<string> = [],
): Language[] {
  const shown = structural(rows, SHOW_KEY);
  const keys = new Set(
    Array.isArray(shown) && shown.length > 0 ? shown.map(String) : fallbackKeys,
  );
  // The reference is never dropped: a site with no fallback language has nothing
  // to fall back to, and V1 keeps FR whatever the list says.
  const kept = languages.filter((l) => keys.has(l.key));
  if (kept.length > 0) return kept;
  return languages.filter((l) => l.key === REFERENCE_KEY);
}

/**
 * Which of them is the reference — the one every other falls back to.
 *
 * The event names it by KEY. Unknown or absent, FR takes over: that is what V1
 * does, and a cascade whose bottom level does not exist resolves to nothing.
 */
export function referenceOf(
  rows: Record<string | number, Extended | null> | undefined,
  active: ReadonlyArray<Language>,
): number {
  const named = structural(rows, DEFAULT_KEY);
  const found =
    typeof named === "string" ? active.find((l) => l.key === named) : undefined;
  return (
    found?.id ??
    active.find((l) => l.key === REFERENCE_KEY)?.id ??
    active[0]?.id ??
    1
  );
}

/**
 * THE EVENT'S PARTICIPANT TYPOLOGIES — `GET /domains/:d/events/:e/typos`.
 *
 * ⚠️ NOT from the settings. `extended.typos` is a leftover: on this event it
 * holds a single entry with the id "test" while the real typologies are 538 to
 * 541, which is why the panel offered none. `typosV2` carries their ids but no
 * names — only this route has both, and it is public, so it costs nothing.
 *
 * A typology with no name is dropped. The route returns whatever the organiser
 * typed, and one of them here is literally called "null" — a row that would sit
 * in the picker meaning nothing to anyone.
 */
export type Typo = { id: number | null; name: string };

export async function fetchTypos(
  domainId: number,
  eventId: number,
  signal?: AbortSignal,
): Promise<Typo[]> {
  const answer = await apiFetch<{
    typos?: Array<{ id?: unknown; label?: unknown }>;
  }>(`/domains/${domainId}/events/${eventId}/typos`, { api: "apix", signal });

  const named = (answer?.typos ?? [])
    .filter((t) => typeof t?.id === "number")
    .map((t) => ({ id: t.id as number, name: String(t.label ?? "").trim() }))
    .filter((t) => t.name.length > 0 && t.name !== "null");

  // "Aucune typologie" is the back-office's wording for the same thing; here the
  // control reads "En tant que", so it says who rather than what is absent.
  return [{ id: null, name: "Tous les participants" }, ...named];
}
